import { clientLogger } from "@ka-astro/utils";
import { SearchOutlineIcon } from "@kds-web/icons";
import { algoliasearch } from "algoliasearch";
import { useEffect, useState, useRef } from "react";
import type { JSX } from "react/jsx-runtime";
import SearchCombobox, {
  type Suggestion,
} from "../SearchCombobox/SearchCombobox";
import transformAlgoliaToSuggestionResults, { type AlgoliaHit } from "../utils";

type Props = {
  onFormSubmit: () => void;
  setCategoryId: (categoryId: number) => void;
  categoryId?: number;
  algoliaAppId: string;
  algoliaApiKey: string;
};

function KeywordSelection({
  onFormSubmit,
  setCategoryId,
  categoryId,
  algoliaApiKey,
  algoliaAppId,
}: Props): JSX.Element {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [shouldSubmit, setShouldSubmit] = useState(false);
  const algoliaRef = useRef<ReturnType<typeof algoliasearch> | null>(null);

  useEffect(() => {
    if (algoliaRef.current === null) {
      algoliaRef.current = algoliasearch(algoliaAppId, algoliaApiKey);
    }
  }, [algoliaApiKey, algoliaAppId]);

  useEffect(() => {
    if (shouldSubmit) {
      onFormSubmit();
      setShouldSubmit(false);
    }
  }, [shouldSubmit, onFormSubmit]);

  function handleSelect(suggestion?: Suggestion): void {
    if (suggestion) {
      setInputValue(suggestion.originalQuery + suggestion.text);
      setCategoryId(suggestion.selectedId);
    }
    setShouldSubmit(true);
  }

  async function handleUserInputChange(userInput: string): Promise<void> {
    setInputValue(userInput);
    await updateSuggestions(userInput);
  }

  async function updateSuggestions(query: string): Promise<void> {
    // Don't search if query is empty
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    try {
      const algoliaResults =
        await algoliaRef.current?.searchSingleIndex<AlgoliaHit>({
          indexName: "ebayk_prod_suggest",
          searchParams: { query },
        });
      // The result can be undefined, if that is the case, we don't want to update the suggestions
      if (!algoliaResults?.hits?.length) {
        setSuggestions([]);
        return;
      }

      const searchResults = transformAlgoliaToSuggestionResults(
        algoliaResults,
        categoryId,
      );

      setSuggestions(searchResults);
    } catch (error) {
      clientLogger.error("Error updating suggestions: ", error);
      setSuggestions([]); // Reset suggestions on error
    }
  }

  return (
    <>
      <SearchOutlineIcon size="medium" className="mx-xsmall" />
      <SearchCombobox
        placeholder="Was suchst du?"
        suggestions={suggestions}
        inputValue={inputValue}
        onInputChange={handleUserInputChange}
        inputName="keywords"
        onSelect={handleSelect}
        listId="search-keyword-list"
      />
    </>
  );
}

export default KeywordSelection;
